import { useEffect } from 'react';
import type { createBrowserRouter } from 'react-router-dom';

type AppRouter = ReturnType<typeof createBrowserRouter>;

/** How often an open tab asks whether a newer build has been deployed. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Brings an open tab onto the newest deploy.
 *
 * A single-page app keeps running the code it loaded, so a tab left open
 * across a deploy (a survey kiosk, say) goes on serving the old build, bugs
 * and all, until someone reloads it. This asks `/version.json` (written by
 * each app's vite.config.ts) every few minutes and whenever the tab comes
 * back into view, and once the deployed build differs from the running one it
 * reloads at the next move to another page.
 *
 * The next page move, not straight away: that is the one moment nothing typed
 * can be lost. The survey saves a page before it moves on, and a kiosk moves
 * off its greeting before the next respondent has typed a thing.
 *
 * It also reloads when the browser cannot fetch a piece of the old build,
 * which the new deploy no longer serves.
 *
 * @param buildId the running build's id, `__BUILD_ID__`. Without a
 *   `/version.json` (local dev) nothing ever reloads.
 */
export function useReloadOnNewVersion(
  router: AppRouter,
  buildId: string,
  intervalMs = VERSION_CHECK_INTERVAL_MS
) {
  useEffect(() => {
    let isStale = false;

    const check = async () => {
      if (isStale) return;
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const deployed: { buildId?: string } = await res.json();
        if (deployed.buildId && deployed.buildId !== buildId) isStale = true;
      } catch {
        // Offline or mid-deploy: the next check will tell.
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    // Vite's event for a chunk that failed to load: the old build asked for a
    // file the new deploy replaced.
    const onPreloadError = (event: Event) => {
      event.preventDefault();
      window.location.reload();
    };

    let pathname = router.state.location.pathname;
    const unsubscribe = router.subscribe((state) => {
      if (state.navigation.state !== 'idle') return;
      if (state.location.pathname === pathname) return;
      pathname = state.location.pathname;
      if (isStale) window.location.reload();
    });

    check();
    const timer = window.setInterval(check, intervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('vite:preloadError', onPreloadError);

    return () => {
      unsubscribe();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, [router, buildId, intervalMs]);
}
